import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { chatAPI, profileAPI } from 'src/api'
import { login as loginAPI, LoginData } from 'src/api/login'
import { logout as logoutAPI } from 'src/api/logout'
import { ProfileType } from 'src/api/profile'
import { APP_STATUS } from 'src/constants/common'
import { initConversationClient } from 'src/services/conversations/client'

// INIT STATE
export interface AccountState {
  isLogin: boolean
  currentUser?: ProfileType
  conversationAccessToken?: string
  login: {
    status: APP_STATUS
    error?: string
    errorCode?: string
  }
  logout: {
    status: APP_STATUS
    error?: string
  }
  getSessionStatus: {
    status: APP_STATUS
    error?: string
  }
  getConversationAccessToken: {
    status: APP_STATUS
    error?: string
  }
}

const initialState: AccountState = {
  isLogin: false,
  login: {
    status: APP_STATUS.INIT,
  },
  logout: {
    status: APP_STATUS.INIT,
  },
  getSessionStatus: {
    status: APP_STATUS.INIT,
  },
  getConversationAccessToken: {
    status: APP_STATUS.INIT,
  },
}

// ACTIONS
const login = createAsyncThunk(
  'account/login',
  async (loginData: LoginData, { dispatch, rejectWithValue }) => {
    const result = await loginAPI(loginData)
    if (result.result) {
      // After successful login, fetch the user profile
      const profileResult = await dispatch(getSessionStatus())
      return profileResult.payload
    } else {
      return rejectWithValue({ errorCode: result.errorCode })
    }
  }
)

const logout = createAsyncThunk('account/logout', async () => {
  const result = await logoutAPI()
  return result
})

const getSessionStatus = createAsyncThunk('account/getSessionStatus', () =>
  profileAPI.getSelfProfile()
)

const getConversationAccessToken = createAsyncThunk('account/getConversationAccessToken', () =>
  chatAPI.getConversationAccessToken()
)

// REDUCERS
const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    // Clear user state (used for logout)
    clearUserState: (state) => {
      state.isLogin = false
      state.currentUser = undefined
      state.conversationAccessToken = undefined
      state.getSessionStatus.status = APP_STATUS.INIT
      state.getConversationAccessToken.status = APP_STATUS.INIT
    },
    // Clear login error
    clearLoginError: (state) => {
      state.login.error = undefined
      state.login.errorCode = undefined
      state.login.status = APP_STATUS.INIT
    },
    // Update current user profile
    updateCurrentUser: (state, action) => {
      if (state.currentUser) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        state.currentUser = { ...state.currentUser, ...action.payload }
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.login.status = APP_STATUS.LOADING
        state.login.error = undefined
        state.login.errorCode = undefined
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLogin = true
        state.currentUser = action.payload as ProfileType
        state.login.status = APP_STATUS.DONE
      })
      .addCase(login.rejected, (state, action) => {
        state.isLogin = false
        state.login.status = APP_STATUS.ERROR
        state.login.error = 'Login failed'
        state.login.errorCode = (action.payload as { errorCode?: string } | undefined)?.errorCode
      })

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.logout.status = APP_STATUS.LOADING
        state.logout.error = undefined
      })
      .addCase(logout.fulfilled, (state) => {
        // Clear all user-related state
        state.isLogin = false
        state.currentUser = undefined
        state.conversationAccessToken = undefined
        state.logout.status = APP_STATUS.DONE
        state.getSessionStatus.status = APP_STATUS.INIT
        state.getConversationAccessToken.status = APP_STATUS.INIT
      })
      .addCase(logout.rejected, (state) => {
        state.logout.status = APP_STATUS.ERROR
        state.logout.error = 'Logout failed'
      })

    // Get session status
    builder
      .addCase(getSessionStatus.pending, (state) => {
        state.getSessionStatus.status = APP_STATUS.LOADING
        state.getSessionStatus.error = undefined
      })
      .addCase(getSessionStatus.fulfilled, (state, action) => {
        const { payload } = action
        // Check if payload is a successful profile response (has id) or a failure response (has message)
        if ('id' in payload && payload.id) {
          state.currentUser = payload
          state.isLogin = true
        } else {
          // This handles when API returns ResponseFail object instead of throwing error
          state.isLogin = false
          state.currentUser = undefined

          state.getSessionStatus.error =
            ('message' in payload && payload.message) || 'Authentication failed' // eslint-disable-line
        }
        state.getSessionStatus.status = APP_STATUS.DONE
      })
      .addCase(getSessionStatus.rejected, (state) => {
        state.isLogin = false
        state.currentUser = undefined
        state.getSessionStatus.status = APP_STATUS.ERROR
        state.getSessionStatus.error = 'Failed to get session status'
      })

    // Get conversation access token
    builder
      .addCase(getConversationAccessToken.pending, (state) => {
        state.getConversationAccessToken.status = APP_STATUS.LOADING
        state.getConversationAccessToken.error = undefined
      })
      .addCase(getConversationAccessToken.fulfilled, (state, action) => {
        const { payload } = action
        if ('accessToken' in payload) {
          initConversationClient(payload.accessToken)
          state.conversationAccessToken = payload.accessToken
          state.getConversationAccessToken.status = APP_STATUS.DONE
        }
      })
      .addCase(getConversationAccessToken.rejected, (state) => {
        state.getConversationAccessToken.status = APP_STATUS.ERROR
        state.getConversationAccessToken.error = 'Failed to get conversation access token'
      })
  },
})

export const { clearUserState, clearLoginError, updateCurrentUser } = accountSlice.actions
export { login, logout, getSessionStatus, getConversationAccessToken }
export default accountSlice.reducer
